import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { DollarSign, TrendingUp, Clock, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/AdminLayout';

export default function DashboardPage() {
  const { apiKey } = useAuth();
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalVolume: 0,
    pendingOrders: 0,
    completedOrders: 0,
    concentratorBalance: 0,
    recentOrders: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000); // Actualizar cada 30s
    return () => clearInterval(interval);
  }, [apiKey]);

  const fetchStats = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Por ahora usamos el endpoint de stats si existe, sino calculamos manualmente
      // Como no tenemos endpoint de stats, haremos requests individuales
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Órdenes',
      value: stats.totalOrders,
      icon: TrendingUp,
      color: 'text-blue-600',
      bg: 'bg-blue-50'
    },
    {
      title: 'Volumen Total',
      value: `$${stats.totalVolume.toFixed(2)}`,
      icon: DollarSign,
      color: 'text-green-600',
      bg: 'bg-green-50'
    },
    {
      title: 'Pendientes',
      value: stats.pendingOrders,
      icon: Clock,
      color: 'text-yellow-600',
      bg: 'bg-yellow-50'
    },
    {
      title: 'Completadas',
      value: stats.completedOrders,
      icon: CheckCircle2,
      color: 'text-[hsl(var(--brand-teal))]',
      bg: 'bg-[hsl(var(--brand-teal))]/10'
    }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[hsl(var(--brand-teal))] mx-auto"></div>
          <p className="mt-4 text-[hsl(var(--muted-foreground))]">Cargando estadísticas...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="dashboard-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-[hsl(var(--brand-ink))]" style={{ fontFamily: 'var(--font-heading)' }}>
            Dashboard
          </h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-1">
            Vista general de tu pasarela de pagos
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="p-6" data-testid={`stat-card-${index}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mb-1">
                      {stat.title}
                    </p>
                    <p className="text-2xl font-bold text-[hsl(var(--brand-ink))]">
                      {stat.value}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${stat.bg}`}>
                    <Icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Welcome Message */}
        <Card className="p-8 text-center">
          <h2 className="text-xl font-semibold text-[hsl(var(--brand-ink))] mb-2">
            ¡Bienvenido a FluxiPay Admin!
          </h2>
          <p className="text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">
            Tu panel de administración está listo. Navega por las diferentes secciones para gestionar tus órdenes, 
            revisar balances y configurar tu pasarela de pagos.
          </p>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6">
            <h3 className="font-semibold text-[hsl(var(--brand-ink))] mb-2">Balance Concentrador</h3>
            <p className="text-3xl font-bold text-[hsl(var(--brand-teal))] mb-2">
              {stats.concentratorBalance} TRX
            </p>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Saldo disponible para fees
            </p>
          </Card>

          <Card className="p-6">
            <h3 className="font-semibold text-[hsl(var(--brand-ink))] mb-2">Tasa de Conversión</h3>
            <p className="text-3xl font-bold text-[hsl(var(--brand-ink))] mb-2">
              {stats.totalOrders > 0 ? ((stats.completedOrders / stats.totalOrders) * 100).toFixed(1) : 0}%
            </p>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Órdenes completadas
            </p>
          </Card>

          <Card className="p-6">
            <h3 className="font-semibold text-[hsl(var(--brand-ink))] mb-2">Red</h3>
            <p className="text-2xl font-bold text-[hsl(var(--brand-ink))] mb-2">
              TRON Testnet
            </p>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Nile Network
            </p>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
